/**
 * Organization Analytics Overview Page
 *
 * Dashboard showing combined analytics for an organization.
 * Route: /org/:orgSlug/analytics
 */

import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGetOrgAnalyticsOverviewQuery } from '../store/api/orgAnalyticsApi';
import { useGetOrganizationBySlugQuery } from '../store/api/trainingApi';
import { LoadErrorNotice } from '../components/LoadErrorNotice';
import { formatRate } from '../utils/scoreDisplay';

type TimePeriod = '7d' | '30d' | '90d' | 'all';

export function OrgAnalyticsOverviewPage() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('30d');

  // Get org ID from slug
  const { data: org, isLoading: loadingOrg } = useGetOrganizationBySlugQuery(orgSlug || '', {
    skip: !orgSlug,
  });

  const since = useMemo(() => {
    if (timePeriod === 'all') return undefined;
    const days = timePeriod === '7d' ? 7 : timePeriod === '30d' ? 30 : 90;
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString();
  }, [timePeriod]);

  const {
    data: overview,
    isLoading: loadingAnalytics,
    isError: analyticsFailed,
    error: analyticsError,
    refetch: refetchAnalytics,
  } = useGetOrgAnalyticsOverviewQuery(
    { orgId: org?.id || '', since },
    { skip: !org?.id }
  );

  if (loadingOrg || loadingAnalytics) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="text-center py-16">
        <h1 className="text-2xl font-bold text-gray-900">Organization not found</h1>
      </div>
    );
  }

  // A failed load is a failure, not an organization with no activity: never
  // fall through to the dashboard, whose counters would all read 0.
  if (analyticsFailed) {
    return (
      <div className="py-16 max-w-xl mx-auto">
        <LoadErrorNotice
          error={analyticsError}
          onRetry={() => void refetchAnalytics()}
          failedMessage="This organization's analytics could not be loaded."
          testIdPrefix="org-analytics"
        />
      </div>
    );
  }

  const training = overview?.training;
  const practice = overview?.practice;
  const coaching = overview?.coaching;
  const usage = overview?.usage;
  const hiring = overview?.hiring;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{org.name} Analytics</h1>
          <p className="mt-1 text-gray-500">Performance metrics and insights</p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
            className="rounded-lg border-gray-300 text-sm"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Training Modules"
          value={training?.totals.modules || 0}
          subtitle={
            training?.completionRate == null
              ? 'Completion rate not available'
              : `${formatRate(training.completionRate)} completion rate`
          }
          linkTo={`/org/${orgSlug}/analytics/training`}
          linkText="View Training"
        />
        <StatCard
          title="Practice Sessions"
          value={practice?.totalSessions || 0}
          subtitle={typeof practice?.avgScores.overall === 'number' ? `${practice.avgScores.overall.toFixed(1)} avg score` : 'avg score not measured'}
        />
        <StatCard
          title="Coaching Sessions"
          value={coaching?.completedSessionCount || 0}
          subtitle={`${coaching?.activeEngagements || 0} active engagements`}
          linkTo={`/org/${orgSlug}/analytics/coaching`}
          linkText="View Coaching"
        />
        <StatCard
          title="Team Members"
          value={usage?.totalMembers || 0}
          subtitle={`${usage?.activeMembers || 0} active recently`}
        />
      </div>

      {/* Training Overview */}
      {training && (
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Training Performance</h2>
            <Link
              to={`/org/${orgSlug}/analytics/training`}
              className="text-sm text-indigo-600 hover:text-indigo-700"
            >
              View Details →
            </Link>
          </div>

          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">
                {training.assignmentStatusCounts.assigned}
              </div>
              <div className="text-sm text-gray-500">Assigned</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {training.assignmentStatusCounts.in_progress}
              </div>
              <div className="text-sm text-gray-500">In Progress</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {training.assignmentStatusCounts.completed}
              </div>
              <div className="text-sm text-gray-500">Completed</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {training.assignmentStatusCounts.failed}
              </div>
              <div className="text-sm text-gray-500">Failed</div>
            </div>
          </div>

          {training.byModule.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Top Modules</h3>
              <div className="space-y-2">
                {training.byModule.slice(0, 5).map((module) => (
                  <div
                    key={module.moduleId}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <span className="font-medium text-gray-900">{module.moduleName}</span>
                      <span className="ml-2 text-sm text-gray-500">
                        {module.assignments} assignments
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-600">
                        {module.completionRate === null ? '— complete' : `${formatRate(module.completionRate)} complete`}
                      </span>
                      {module.avgOverallScore && (
                        <span className="text-sm font-medium text-indigo-600">
                          {module.avgOverallScore.toFixed(1)} avg
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Coaching Overview */}
      {coaching && (
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Coaching Activity</h2>
            <Link
              to={`/org/${orgSlug}/analytics/coaching`}
              className="text-sm text-indigo-600 hover:text-indigo-700"
            >
              View Details →
            </Link>
          </div>

          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">
                {coaching.coachingSessions.requested}
              </div>
              <div className="text-sm text-gray-500">Requested</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {coaching.coachingSessions.scheduled}
              </div>
              <div className="text-sm text-gray-500">Scheduled</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {coaching.coachingSessions.completed}
              </div>
              <div className="text-sm text-gray-500">Completed</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {coaching.coachingSessions.cancelled}
              </div>
              <div className="text-sm text-gray-500">Cancelled</div>
            </div>
          </div>

          {coaching.recentCompletedSessions.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Recent Sessions</h3>
              <div className="space-y-2">
                {coaching.recentCompletedSessions.slice(0, 5).map((session) => (
                  <div
                    key={session.bookingId}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <span className="font-medium text-gray-900">{session.clientName}</span>
                      <span className="mx-2 text-gray-400">with</span>
                      <span className="text-gray-700">{session.coachName}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-500">
                        {new Date(session.completedAt).toLocaleDateString()}
                      </span>
                      {session.lastScoreOverall && (
                        <span className="text-sm font-medium text-indigo-600">
                          {session.lastScoreOverall.toFixed(1)} score
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Hiring Overview */}
      {hiring && hiring.totalPipelines > 0 && (
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Hiring Pipeline</h2>
          </div>

          <div className="grid grid-cols-6 gap-4 mb-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">
                {hiring.applicationsByStage.applied}
              </div>
              <div className="text-sm text-gray-500">Applied</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {hiring.applicationsByStage.shortlisted}
              </div>
              <div className="text-sm text-gray-500">Shortlisted</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                {hiring.applicationsByStage.interviewing}
              </div>
              <div className="text-sm text-gray-500">Interviewing</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {hiring.applicationsByStage.offer}
              </div>
              <div className="text-sm text-gray-500">Offer</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {hiring.applicationsByStage.hired}
              </div>
              <div className="text-sm text-gray-500">Hired</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {hiring.applicationsByStage.rejected}
              </div>
              <div className="text-sm text-gray-500">Rejected</div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-indigo-50 rounded-lg">
              <div className="text-2xl font-bold text-indigo-600">
                {hiring.totalPipelines}
              </div>
              <div className="text-sm text-gray-500">Total Pipelines</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {hiring.openPipelines}
              </div>
              <div className="text-sm text-gray-500">Open Pipelines</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {(hiring.hireConversionRate * 100).toFixed(0)}%
              </div>
              <div className="text-sm text-gray-500">Hire Rate</div>
            </div>
          </div>

          {hiring.recentHires.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Recent Hires</h3>
              <div className="space-y-2">
                {hiring.recentHires.slice(0, 5).map((hire) => (
                  <div
                    key={hire.applicationId}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <span className="font-medium text-gray-900">{hire.candidateName}</span>
                      <span className="mx-2 text-gray-400">for</span>
                      <span className="text-gray-700">{hire.pipelineTitle}</span>
                      {hire.verifiedAtApply && (
                        <span className="ml-2 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                          Verified
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(hire.hiredAt).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Average Scores */}
      {training?.averageScores && (
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Average Scores</h2>
          <div className="grid grid-cols-5 gap-4">
            <ScoreCard label="Overall" value={training.averageScores.overall} />
            <ScoreCard label="Clarity" value={training.averageScores.clarity} />
            <ScoreCard label="Structure" value={training.averageScores.structure} />
            <ScoreCard label="Accuracy" value={training.averageScores.accuracy} />
            <ScoreCard label="Complexity" value={training.averageScores.complexity} />
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  title,
  value,
  subtitle,
  linkTo,
  linkText,
}: {
  title: string;
  value: number;
  subtitle: string;
  linkTo?: string;
  linkText?: string;
}) {
  return (
    <div className="bg-white rounded-lg border p-6">
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <div className="mt-2 text-3xl font-bold text-gray-900">{value}</div>
      <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
      {linkTo && linkText && (
        <Link
          to={linkTo}
          className="mt-3 inline-block text-sm text-indigo-600 hover:text-indigo-700"
        >
          {linkText} →
        </Link>
      )}
    </div>
  );
}

function ScoreCard({ label, value }: { label: string; value?: number }) {
  const displayValue = value != null ? value.toFixed(1) : '-';
  const color = value != null && value >= 70 ? 'text-green-600' : value != null && value >= 50 ? 'text-yellow-600' : 'text-gray-400';

  return (
    <div className="text-center p-4 bg-gray-50 rounded-lg">
      <div className={`text-2xl font-bold ${color}`}>{displayValue}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  );
}

export default OrgAnalyticsOverviewPage;
